import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Check, ArrowLeft, Zap, Shield, Globe, Cpu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrandMark } from './BrandMark';

export function Pricing() {
    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
        <div className="p-4 md:p-6 space-y-6 w-full min-h-screen relative overflow-hidden bg-[#0a0b14] text-white">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-600/10 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-purple-600/10 blur-[120px] pointer-events-none" />
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-overlay pointer-events-none" />
            
            <div className="max-w-7xl mx-auto relative z-10 pt-8">
                <div className="flex items-center justify-center gap-4 mb-10">
                    <BrandMark className="w-[72px] h-[72px] drop-shadow-[0_0_18px_rgba(59,130,246,0.45)]" />
                    <div className="text-left hidden sm:block">
                        <p className="text-3xl font-black tracking-[-0.02em] text-blue-100/95 leading-none">3RDI</p>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-blue-100/45 mt-1 font-semibold">Vision Intelligence Platform</p>
                    </div>
                </div>
                <div className="relative mb-16">
                    <Button
                        onClick={handleGoBack}
                        variant="ghost"
                        size="sm"
                        className="absolute left-0 top-0 text-blue-100/40 hover:text-white hover:bg-white/5 transition-all rounded-xl"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Return to Hub
                    </Button>

                    <div className="text-center pt-16 md:pt-4">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center p-3 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-6 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                            <Shield className="w-8 h-8 text-blue-400" />
                        </motion.div>
                        <h1 className="text-4xl md:text-6xl font-black mb-4 uppercase tracking-tighter">
                            Enterprise Deployment Plans
                        </h1>
                        <p className="text-sm md:text-base font-bold text-blue-100/40 uppercase tracking-[0.2em] max-w-3xl mx-auto">
                            Select the scale of your cognitive neural grid
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* SCOUT Plan */}
                    <div className="rounded-3xl overflow-hidden bg-black/40 border border-white/5 backdrop-blur-2xl flex flex-col hover:border-blue-500/30 transition-all duration-500 hover:-translate-y-2 group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="p-8 flex flex-col h-full relative z-10 text-center">
                            <h3 className="text-[10px] font-black tracking-[0.2em] text-blue-400 mb-6 uppercase">Scout Grid</h3>
                            <div className="mb-8">
                                <p className="text-4xl font-black tracking-tighter text-white">
                                    $49<span className="text-sm text-blue-100/40 font-bold uppercase tracking-widest ml-1">/mo</span>
                                </p>
                            </div>

                            <Button className="font-black text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/10 text-white w-full mb-8 border border-white/10 py-6 rounded-2xl transition-all">
                                Initialize Hub
                            </Button>

                            <div className="mb-6">
                                <div className="text-3xl font-black text-white font-mono tracking-tighter">5</div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-blue-400 mt-1">
                                    Deployed Nodes
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="text-xl font-bold text-white font-mono tracking-tighter">48<span className="text-[10px] text-blue-100/40">h</span></div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-400/60 mt-1">
                                    Archive Retention
                                </div>
                            </div>

                            <div className="mt-auto space-y-4 pt-6 border-t border-white/5 text-left">
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>Standard Threat Detection</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>Basic Email Routing</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>1 Central Command Node</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TACTICAL Plan */}
                    <div className="rounded-3xl overflow-hidden bg-black/40 border border-white/5 backdrop-blur-2xl flex flex-col hover:border-blue-500/30 transition-all duration-500 hover:-translate-y-2 group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="p-8 flex flex-col h-full relative z-10 text-center">
                            <h3 className="text-[10px] font-black tracking-[0.2em] text-emerald-400 mb-6 uppercase">Tactical Grid</h3>
                            <div className="mb-8">
                                <p className="text-4xl font-black tracking-tighter text-white">
                                    $149<span className="text-sm text-blue-100/40 font-bold uppercase tracking-widest ml-1">/mo</span>
                                </p>
                            </div>

                            <Button className="font-black text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/10 text-white w-full mb-8 border border-white/10 py-6 rounded-2xl transition-all">
                                Initialize Hub
                            </Button>

                            <div className="mb-6">
                                <div className="text-3xl font-black text-white font-mono tracking-tighter">25</div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-400 mt-1">
                                    Deployed Nodes
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="text-xl font-bold text-white font-mono tracking-tighter">14<span className="text-[10px] text-blue-100/40">d</span></div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-400/60 mt-1">
                                    Archive Retention
                                </div>
                            </div>

                            <div className="mt-auto space-y-4 pt-6 border-t border-white/5 text-left">
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                    <span>Advanced Cognitive Matching</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                    <span>Tactical SMS Responding</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                    <span>3 Command Terminal Seats</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* COMMAND Plan - Highlighted */}
                    <div className="rounded-3xl overflow-hidden bg-blue-600/10 border border-blue-500/40 backdrop-blur-3xl flex flex-col relative shadow-[0_0_50px_rgba(37,99,235,0.2)] transform lg:scale-105 z-20">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-[80px] pointer-events-none" />
                        <div className="absolute top-0 left-0 right-0 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[9px] font-black tracking-[0.3em] text-center uppercase">
                            Primary Engagement Sector
                        </div>
                        <div className="p-8 pt-12 flex flex-col h-full relative z-10 text-center">
                            <h3 className="text-[10px] font-black tracking-[0.2em] text-white/90 mb-6 uppercase flex justify-center items-center gap-2"><Zap className="w-3.5 h-3.5 text-blue-400"/> Command Matrix</h3>
                            <div className="mb-8">
                                <p className="text-5xl font-black flex justify-center items-start text-transparent bg-clip-text bg-gradient-to-br from-white to-blue-300 drop-shadow-[0_0_10px_rgba(59,130,246,0.5)] tracking-tighter">
                                    $499<span className="text-xs text-blue-200 uppercase tracking-widest font-bold mt-2 ml-1">/mo</span>
                                </p>
                            </div>

                            <Button className="w-full mb-8 py-7 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black text-[10px] uppercase tracking-widest transition-all shadow-[0_0_30px_rgba(37,99,235,0.4)]">
                                Establish Link
                            </Button>

                            <div className="mb-6">
                                <div className="text-4xl font-black text-white font-mono tracking-tighter drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">100</div>
                                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-300 mt-2">
                                    Deployed Nodes
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="text-2xl font-bold text-white font-mono tracking-tighter">90<span className="text-[10px] text-blue-100/40">d</span></div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-blue-200/60 mt-1">
                                    Archive Retention
                                </div>
                            </div>

                            <div className="mt-auto space-y-4 pt-6 border-t border-blue-500/20 text-left">
                                <div className="flex items-start gap-3 text-white text-xs font-light">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>Predictive Neural Anomalies</span>
                                </div>
                                <div className="flex items-start gap-3 text-white text-xs font-light">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>Automated Hardware Control</span>
                                </div>
                                <div className="flex items-start gap-3 text-white text-xs font-light">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>Local Edge Compute Option</span>
                                </div>
                                <div className="flex items-start gap-3 text-white text-xs font-medium">
                                    <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <span>Direct Line Tactical Support</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* APEX Plan */}
                    <div className="rounded-3xl overflow-hidden bg-black/40 border border-white/5 backdrop-blur-2xl flex flex-col hover:border-purple-500/30 transition-all duration-500 hover:-translate-y-2 group lg:translate-x-4">
                         <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="p-8 flex flex-col h-full relative z-10 text-center">
                            <h3 className="text-[10px] font-black tracking-[0.2em] text-purple-400 mb-6 uppercase flex justify-center items-center gap-2"><Globe className="w-3.5 h-3.5" /> Apex Grid</h3>
                            <div className="mb-8 flex items-center justify-center min-h-[50px]">
                                <p className="text-xl font-bold text-white uppercase tracking-widest">
                                    Custom Scale
                                </p>
                            </div>

                            <Button className="font-black text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/10 text-white w-full mb-8 border border-white/10 py-6 rounded-2xl transition-all hover:border-purple-500/50">
                                Contact Architecture Team
                            </Button>

                            <div className="mb-6">
                                <div className="text-3xl font-black text-white font-mono tracking-tighter flex justify-center items-center"><Cpu className="w-6 h-6 text-purple-400 mr-2"/> &infin;</div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-purple-400 mt-2">
                                    Infinite Nodes
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="text-xl font-bold text-white font-mono tracking-tighter">Perpetual</div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-purple-400/60 mt-1">
                                    Archive Retention
                                </div>
                            </div>

                            <div className="mt-auto space-y-4 pt-6 border-t border-white/5 text-left">
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-purple-400 flex-shrink-0" />
                                    <span>Dedicated Neural Instances</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-purple-400 flex-shrink-0" />
                                    <span>Custom Model Training</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-purple-400 flex-shrink-0" />
                                    <span>On-Premise Vaulting</span>
                                </div>
                                <div className="flex items-start gap-3 text-blue-100/60 text-xs font-light">
                                    <Check className="h-4 w-4 text-purple-400 flex-shrink-0" />
                                    <span>24/7 Red-Team Resilience</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-16 text-center pb-12">
                    <p className="text-[10px] text-blue-100/20 font-black uppercase tracking-[0.2em]">
                        * Hardware arrays and optical sensors are licensed separately via certified vendors.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Pricing;
